import { showGlobalToast } from '../context/ToastContext';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  class ApiClient {
    constructor() {
      this.currentAgentId = localStorage.getItem('activeAgentId') || null;
    }

    setAgentId(id) {
      if (id) {
        this.currentAgentId = id.toString();
        localStorage.setItem('activeAgentId', this.currentAgentId);
      }
    }

    getAuthToken() {
      const user = sessionStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        return userData.token;
      }
      return null;
    }

    get(endpoint, options = {}) {
      return this.request(endpoint, { ...options, method: 'GET' });
    }

    post(endpoint, body = {}, options = {}) {
      return this.request(endpoint, {
        ...options,
        method: 'POST',
        body: JSON.stringify(body),
      });
    }

    put(endpoint, body = {}, options = {}) {
      return this.request(endpoint, {
        ...options,
        method: 'PUT',
        body: JSON.stringify(body),
      });
    }

    delete(endpoint, options = {}) {
      return this.request(endpoint, { ...options, method: 'DELETE' });
    }

    async request(endpoint, options = {}) {
      const cleanEndpoint = (endpoint.startsWith('/api') && API_BASE_URL.endsWith('/api')) 
        ? endpoint.substring(4) 
        : endpoint;
      const url = `${API_BASE_URL}${cleanEndpoint}`;
      const token = this.getAuthToken();
      
      // Use the explicitly set agentId, or fallback to what's in options/localStorage
      const agentId = options.agentId || this.currentAgentId;
      
      const activeTenantId = localStorage.getItem('activeTenantId');
      
      // Normalize headers to prevent duplication (e.g., X-Agent-ID vs X-Agent-ID)
      const normalizedHeaders = {
        'Content-Type': 'application/json',
        'X-Agent-ID': agentId,
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...(activeTenantId && { 'X-Tenant-ID': activeTenantId }),
      };

      // Merge options.headers while removing any existing agent ID keys to prevent comma-concatenation
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          const lowerKey = key.toLowerCase();
          if (lowerKey !== 'x-agent-id' && lowerKey !== 'x-agent-id') { // Note: handle common variants
            normalizedHeaders[key] = options.headers[key];
          }
        });
      }

      const config = {
        ...options,
        headers: normalizedHeaders
      };
      try {
        const response = await fetch(url, config);
        if (response.status === 401) {
          // Auto logout on unauthorized — clear ALL state
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/login';
          throw new Error('Session expired. Please login again.');
        }
        if (!response.ok) {
          try {
            const errorData = await response.json();
            if (errorData.detail) {
              const detail = errorData.detail;
              if (Array.isArray(detail)) {
                // FastAPI 422 validation error detail is an array of objects
                const messages = detail.map(err => {
                  // Clean up location path (remove 'body' prefix if present)
                  const path = err.loc ? err.loc.filter(l => l !== 'body').join('.') : '';
                  return path ? `${path}: ${err.msg}` : err.msg;
                });
                throw new Error(messages.join(' | '));
              }
              throw new Error(typeof detail === 'object' ? JSON.stringify(detail) : detail);
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          } catch (parseError) {
            if (parseError.message && !parseError.message.includes('HTTP error')) {
              throw parseError;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }
        return await response.json();
      } catch (error) {
        console.error('API request failed:', error);
        showGlobalToast(error.message || 'An unexpected error occurred', 'error');
        throw error;
      }
    }

    // Logo APIs
    async uploadLogo(file) {
      const formData = new FormData();
      formData.append('file', file);
      const token = this.getAuthToken();
      const agentId = this.currentAgentId;

      const response = await fetch(`${API_BASE_URL}/logo/upload`, {
        method: 'PUT',
        headers: {
          'X-Agent-ID': agentId,
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = typeof errorData.detail === 'object' 
          ? JSON.stringify(errorData.detail) 
          : (errorData.detail || `Upload failed with status ${response.status}`);
        throw new Error(errorMessage);
      }
      
      return await response.json();
    }

    async getCurrentLogo() {
      const data = await this.request('/logo/config');
      if (data && data.logo_url) {
        if (data.logo_url.startsWith('/uploads') || data.logo_url.startsWith('/logo/')) {
          data.logo_url = `${API_BASE_URL}${data.logo_url}`;
        }
      }
      return data;
    }

    // Document APIs
    async uploadDocuments(files, metadata = {}) {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      if (Object.keys(metadata).length > 0) {
        formData.append('metadata', JSON.stringify(metadata));
      }

      const token = this.getAuthToken();
      const agentId = this.currentAgentId;
      
      const response = await fetch(`${API_BASE_URL}/documents/upload`, {
        method: 'POST',
        headers: {
          'X-Agent-ID': agentId,
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = typeof errorData.detail === 'object' 
          ? JSON.stringify(errorData.detail) 
          : (errorData.detail || `Document upload failed with status ${response.status}`);
        throw new Error(errorMessage);
      }

      return await response.json();
    }

    async getDocuments() {
      return this.request('/documents/all');
    }

    async getKnowledgeStats() {
      return this.request('/documents/all').then(data => {
        const docs = data.documents || [];
        return {
          total_documents: docs.length,
          processed_documents: docs.filter(d => d.status === 'completed').length,
          total_size_bytes: docs.reduce((acc, d) => acc + (d.file_size || 0), 0)
        };
      });
    }

    async deleteDocument(docId) {
      return this.request(`/documents/${docId}`, { method: 'DELETE' });
    }

    async processDocument(docId) {
      return this.request(`/documents/process/${docId}`, { method: 'POST' });
    }

    async getDocumentStatus(docId) {
      return this.request(`/documents/status/${docId}`);
    }



    // Widget Access APIs (for Admin/Agent dashboards)
    async getWidgetSessions(agentId, page = 1, limit = 10, escalated = false) {
      return this.request(`/widget/sessions?agent_id=${agentId}&page=${page}&limit=${limit}${escalated ? '&escalated=true' : ''}`);
    }

    async getWidgetHistory(sessionId, agentId) {
      return this.request(`/widget/chat/history/${sessionId}?agent_id=${agentId}`);
    }

    async deleteWidgetSession(sessionId, agentId) {
      return this.request(`/widget/sessions/${sessionId}?agent_id=${agentId}`, {
        method: 'DELETE'
      });
    }

    // Widget Admin APIs
    async getContacts(page = 1, limit = 10, search = '') {
      return this.request(`/widget/admin/contacts?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
    }

    async getWidgetConfig() {
      return this.request('/widget/admin/config');
    }

    async updateWidgetConfig(config) {
      return this.request('/widget/admin/config', {
        method: 'PUT',
        body: JSON.stringify(config),
      });
    }

    async patchWidgetConfig(config) {
      return this.request('/widget/admin/config', {
        method: 'PATCH',
        body: JSON.stringify(config),
      });
    }
    






    // API Tools APIs
    async getApiTools() {
      return this.request('/tools');
    }

    async createApiTool(tool) {
      return this.request('/tools', {
        method: 'POST',
        body: JSON.stringify(tool),
      });
    }

    async updateApiTool(toolId, tool) {
      return this.request(`/tools/${toolId}`, {
        method: 'PUT',
        body: JSON.stringify(tool),
      });
    }

    async toggleApiTool(toolId, isActive) {
      return this.request(`/tools/${toolId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: isActive }),
      });
    }

    async deleteApiTool(toolId) {
      return this.request(`/tools/${toolId}`, {
        method: 'DELETE',
      });
    }

    async testApiTool(toolId, inputs) {
      return this.request(`/tools/${toolId}/test`, {
        method: 'POST',
        body: JSON.stringify(inputs),
      });
    }

    // Aliases for compatibility
    getTools() { return this.getApiTools(); }
    createTool(tool) { return this.createApiTool(tool); }
    updateTool(toolId, tool) { return this.updateApiTool(toolId, tool); }
    deleteTool(toolId) { return this.deleteApiTool(toolId); }
    toggleTool(toolId, isActive) { return this.toggleApiTool(toolId, isActive); }
    testTool(toolId, inputs) { return this.testApiTool(toolId, inputs); }

    // Domain Management APIs
    async getAgents() {
      return this.request('/agents');
    }

    async getDomains() {
      return this.request('/domains');
    }

    async addDomain(domain) {
      return this.request('/domains', {
        method: 'POST',
        body: JSON.stringify({ domain }),
      });
    }

    async deleteDomain(domainId) {
      return this.request(`/domains/${domainId}`, {
        method: 'DELETE',
      });
    }

    // Agent APIs
    async getAgentSessions() {
      return this.request('/agent-chat/sessions');
    }

    async getAgentMessages(sessionId) {
      return this.request(`/agent-chat/messages/${sessionId}`);
    }

    async sendAgentMessage(sessionId, message) {
      return this.request('/agent-chat/send', {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId, message }),
      });
    }

    async closeAgentSession(sessionId) {
      return this.request(`/agent-chat/close/${sessionId}`, {
        method: 'POST',
      });
    }

    // Knowledge Base (Smart FAQ) APIs
    async getKBTopics() {
      return this.request('/knowledge-base/topics');
    }

    async createKBTopic(name, description) {
      return this.request('/knowledge-base/topics', {
        method: 'POST',
        body: JSON.stringify({ name, description }),
      });
    }

    async deleteKBTopic(topicId) {
      return this.request(`/knowledge-base/topics/${topicId}`, {
        method: 'DELETE',
      });
    }

    async getKBQuestions(topicId) {
      return this.request(`/knowledge-base/questions/${topicId}`);
    }

    async createKBQuestion(topicId, text) {
      return this.request('/knowledge-base/questions', {
        method: 'POST',
        body: JSON.stringify({ topic_id: topicId, text }),
      });
    }

    async deleteKBQuestion(questionId) {
      return this.request(`/knowledge-base/questions/${questionId}`, {
        method: 'DELETE',
      });
    }

    async createKBResponse(questionId, instructionText, triggerCondition) {
      return this.request('/knowledge-base/responses', {
        method: 'POST',
        body: JSON.stringify({
          question_id: questionId,
          instruction_text: instructionText,
          trigger_condition: triggerCondition
        }),
      });
    }

    async deleteKBResponse(responseId) {
      return this.request(`/knowledge-base/responses/${responseId}`, {
        method: 'DELETE',
      });
    }

    async updateKBQuestion(questionId, text) {
      return this.request(`/knowledge-base/questions/${questionId}`, {
        method: 'PUT',
        body: JSON.stringify({ text }),
      });
    }

    async updateKBResponse(responseId, instructionText, triggerCondition) {
      return this.request(`/knowledge-base/responses/${responseId}`, {
        method: 'PUT',
        body: JSON.stringify({
          instruction_text: instructionText,
          trigger_condition: triggerCondition
        }),
      });
    }

    async generateFAQVariations(topic, question) {
      return this.request('/knowledge-base/generate-faq', {
        method: 'POST',
        body: JSON.stringify({ topic, question }),
      });
    }

    async getInstructions() {
      return this.request('/instructions');
    }

    async getInstructionVersions() {
      return this.request('/instructions/versions');
    }

    async createInstructions(content, versionName) {
      return this.request('/instructions', {
        method: 'POST',
        body: JSON.stringify({ content, version_name: versionName }),
      });
    }

    async activateInstruction(instructionId) {
      return this.request(`/instructions/activate/${instructionId}`, {
        method: 'POST',
      });
    }

    async deleteInstruction(instructionId) {
      return this.request(`/instructions/${instructionId}`, {
        method: 'DELETE',
      });
    }

    async improvePrompt(prompt) {
      return this.request('/instructions/improve', {
        method: 'POST',
        body: JSON.stringify({ prompt }),
      });
    }

    // Campaign APIs
    async getCampaigns() {
      return this.request('/campaigns');
    }

    async createCampaign(campaign) {
      // Split priority from the rest of the campaign data as expected by backend
      const { priority, ...data } = campaign;
      return this.request('/campaigns', {
        method: 'POST',
        body: JSON.stringify({ 
          priority: priority || 0, 
          data: data 
        }),
      });
    }

    async updateCampaign(id, campaign) {
      // Handle partial updates by extracting top-level fields
      const { priority, is_active, ...data } = campaign;
      const payload = {};
      
      if (priority !== undefined) payload.priority = priority;
      if (is_active !== undefined) payload.is_active = is_active;
      
      // Only include data if there are other fields besides priority/is_active
      if (Object.keys(data).length > 0) {
        payload.data = data;
      }
      
      return this.request(`/campaigns/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    }

    async deleteCampaign(id) {
      return this.request(`/campaigns/${id}`, {
        method: 'DELETE',
      });
    }

    async getLiveCampaign() {
      return this.request('/campaigns/live');
    }



    // Business Flow APIs
    async getFlows() {
      return this.request('/flows');
    }

    async saveFlow(flowData) {
      return this.request('/flows', {
        method: 'POST',
        body: JSON.stringify(flowData),
      });
    }

    async updateFlow(flowId, flowData) {
      return this.request(`/flows/${flowId}`, {
        method: 'PUT',
        body: JSON.stringify(flowData),
      });
    }

    async deleteFlow(flowId) {
      return this.request(`/flows/${flowId}`, {
        method: 'DELETE',
      });
    }

    async getFlowDetail(flowId) {
      return this.request(`/flows/${flowId}`);
    }

  }

  const apiClient = new ApiClient();
  export default apiClient;